import { post } from "../../helper/Api_helper";
import {
  RESUME_PARSER_BASE_URL,
  PARSED_RESUME_URL,
  PROCESS_UPLOAD_FILE,
  VYLABS_BASE_URL,
  CREATE_RUN_SET_ID,
  GET_UNPARSED_RESUMES_COUNT,
  WEB_SOCKET_URL,
} from "../../helper/url_helper";
import envConfig from "../../helper/env_config";

const staticIrmtVendorId = envConfig.irmtCandidateVendorId;

const getFileNameFromPath = (path = "") => {
  if (!path || typeof path !== "string") return "";
  const segments = path.split("/");
  return segments[segments.length - 1] || "";
};

export const downloadResumeFromPresignedUrl = async (
  presignedUrl,
  originalPath = "",
  candidateName = ""
) => {
  if (!presignedUrl) return null;

  try {
    const response = await fetch(presignedUrl);
    if (!response.ok) {
      throw new Error("Failed to download resume from presigned URL");
    }

    const blob = await response.blob();
    const originalName = getFileNameFromPath(originalPath);

    const extension = blob.type.includes("pdf") ? "pdf" : (blob.type.includes("word") ? "docx" : "txt");

    const fileName =
      originalName || `${candidateName || "irmt_resume"}.${extension}`;
    const contentType = blob.type || "application/octet-stream";
    const file = new File([blob], fileName, { type: contentType });
    file.localUrl = URL.createObjectURL(blob);
    return file;
  } catch (error) {
    console.error("Failed to create resume file from presigned URL:", error);
    return null;
  }
};

async function processUploadedFile(fileNames, runSetId, token) {
  const requestUrl = RESUME_PARSER_BASE_URL + PROCESS_UPLOAD_FILE;
  const requestBodyData = {
    fileNames: fileNames,
    runSetID: runSetId,
    vendorID: staticIrmtVendorId,
  };

  const requestBody = JSON.stringify(requestBodyData);
  try {
    const response = await fetch(requestUrl, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
      },
      body: requestBody,
    });
    return response.ok;
  } catch (error) {
    console.error("[IRMT Parser] Error processing file:", error);
    return null;
  }
}

export const parseIrmtResumeHeadless = async (file, options = {}) => {
  const signal = options.signal;
  let ws;
  let pingInterval;
  let pingTimeout;

  const closeWebSocket = () => {
    try {
      if (ws) ws.close();
    } catch (e) {}
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
    if (pingTimeout) {
      clearTimeout(pingTimeout);
      pingTimeout = null;
    }
  };

  try {
    if (!file) {
      return { error: "No resume file available for parsing" };
    }

    if (signal?.aborted) {
      return { error: "Operation aborted before start" };
    }

    const appToken = localStorage.getItem("token");
    if (!appToken) {
      return { error: "No app token found. Please login." };
    }

    const openWebSocket = () => {
      try {
        const wsProtocol =
          window.location.protocol === "https:" ? "wss://" : "wss://";
        const baseHost = String(WEB_SOCKET_URL)
          .replace(/^wss?:\/\//, "")
          .replace(/^https?:\/\//, "")
          .replace(/^\/+/, "");
        const wsUrl = `${wsProtocol}${baseHost}/ws?token=${encodeURIComponent(
          "Bearer " + appToken
        )}`;

        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          const sendPing = () => {
            if (ws && ws.readyState === WebSocket.OPEN && !signal?.aborted) {
              ws.send("ping");
              if (pingTimeout) clearTimeout(pingTimeout);
              pingTimeout = setTimeout(() => {
                try {
                  ws.close(1000, "Ping timeout");
                } catch (e) {}
              }, 10000);
            }
          };
          sendPing();
          pingInterval = setInterval(() => {
            if (!signal?.aborted) {
              sendPing();
            } else {
              clearInterval(pingInterval);
            }
          }, 30000);
        };

        ws.onmessage = (evt) => {
          if (evt.data === "pong") {
            if (pingTimeout) {
              clearTimeout(pingTimeout);
              pingTimeout = null;
            }
          }
        };

        ws.onclose = () => {
          if (pingInterval) {
            clearInterval(pingInterval);
            pingInterval = null;
          }
          if (pingTimeout) {
            clearTimeout(pingTimeout);
            pingTimeout = null;
          }
        };

        ws.onerror = (e) => {
          console.error("WebSocket error:", e);
        };
      } catch (e) {
        console.warn("[IrmtHeadlessParser] Failed to open WS", e);
      }
    };

    openWebSocket();

    if (signal) {
      signal.addEventListener("abort", () => {
        closeWebSocket();
      });
    }

    if (signal?.aborted) return { error: "Operation aborted" };

    const runSetId = await post(VYLABS_BASE_URL, CREATE_RUN_SET_ID, {
      folderName: file?.name || "irmt-import",
      totalFiles: 1,
    });

    if (!runSetId) {
      closeWebSocket();
      return { error: "Failed to create run set" };
    }

    const requestBody = JSON.stringify({ FileNames: [file.name] });
    const presignedResp = await fetch(
      RESUME_PARSER_BASE_URL + PARSED_RESUME_URL,
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + appToken,
          "Content-Type": "application/json",
        },
        body: requestBody,
      }
    );

    if (!presignedResp.ok) {
      closeWebSocket();
      return { error: "Failed to get presigned URL" };
    }

    const presignedData = await presignedResp.json();
    const parserUploadUrl = presignedData?.PresignedUploadURLs?.[0];

    if (!parserUploadUrl) {
      closeWebSocket();
      return { error: "No presigned URL returned" };
    }

    const uploadResp = await fetch(parserUploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": "application/octet-stream" },
      mode: "cors",
    });

    if (!uploadResp.ok) {
      closeWebSocket();
      return { error: "Failed to upload to presigned URL" };
    }

    const processResp = await processUploadedFile([file.name], runSetId, appToken);
    if (!processResp) {
      closeWebSocket();
      return { error: "Failed to start processing via processUploadedFile" };
    }

    await new Promise((r) => setTimeout(r, 20000));

    const MAX_ATTEMPTS = 3;
    let finalResult = null;
    let lastFailReason = null;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (signal?.aborted) {
        closeWebSocket();
        return { error: "Operation aborted during polling" };
      }

      try {
        const status = await post(VYLABS_BASE_URL, GET_UNPARSED_RESUMES_COUNT, {
          runSetID: runSetId,
        });
        const completed = status?.completed_files || [];
        const failed = status?.failed_files || [];
        const incomplete = status?.incomplete_files || [];

        const completedFile = completed.find(
          (f) => f?.name === file.name && f?.candidate_id
        );
        if (completedFile?.candidate_id) {
          finalResult = {
            candidateId: completedFile.candidate_id,
            fileID: completedFile.id || completedFile.file_id || completedFile.fileID,
          };
          break;
        }

        const incompleteFile = incomplete.find((f) => f?.name === file.name);
        if (incompleteFile?.id) {
          finalResult = {
            status: "INCOMPLETE",
            candidateId: null,
            fileID: incompleteFile.id,
          };
          break;
        }

        const failedFile = failed.find((f) => f?.name === file.name);
        if (failedFile) {
          if (failedFile.id) {
            finalResult = {
              status: "FAILED",
              candidateId: null,
              fileID: failedFile.id,
            };
          }
          lastFailReason = failedFile?.message || "parse_failed";
        }
      } catch (e) {
        console.warn(`Status check attempt ${attempt + 1} failed:`, e);
        lastFailReason = e.message || "Status check failed";
      }

      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise((r) => setTimeout(r, 20000));
      }
    }

    closeWebSocket();
    if (finalResult) {
      return finalResult;
    }

    return {
      error: lastFailReason || "Parsing timeout without candidate_id",
      isDefinitiveFailure: true,
    };
  } catch (err) {
    closeWebSocket();
    if (err.name === "AbortError" || signal?.aborted) {
      return { error: "Operation aborted" };
    }
    return {
      error: err?.message || "Unknown headless parse error",
      isDefinitiveFailure: true,
    };
  }
};

